import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/joy';

import CrewModal from './CrewModal';

export type InstrumentSettings = {
  attitudeIndicator: boolean;
  compass: boolean;
  thrust: boolean;
  battery: boolean;
};

function CrewInstrumentSettingsModal({
  open,
  onClose,
  instrumentSettings,
  setInstrumentSettings
}: {
  open: boolean;
  onClose: () => void;
  instrumentSettings: InstrumentSettings;
  setInstrumentSettings: (instrumentSettings: InstrumentSettings) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const SwitchElement = ({
    checked,
    onChange,
    label
  }: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
  }): React.ReactElement => {
    return (
      <label htmlFor={label} className='inline-flex items-center justify-between cursor-pointer'>
        <Typography level='body-md' className='font-medium' sx={{ color: 'common.white' }}>
          {label}
        </Typography>
        <input
          id={label}
          type='checkbox'
          className='sr-only peer'
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <Box
          className="relative w-11 h-6 rounded-full peer peer-focus:ring-4 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all"
          sx={{
            bgcolor: 'neutral.800',
            'peer-focus:ring-color': 'success.300',
            '&:after': {
              bgcolor: 'common.white',
              border: 1,
              borderColor: 'neutral.800'
            },
            '.peer:checked ~ &': {
              bgcolor: 'success.400'
            },
            '.peer:checked ~ &:after': {
              borderColor: 'common.white'
            }
          }}
        />
      </label>
    );
  };
  return (
    <CrewModal open={open} onClose={onClose} title={t('crew_view.widget_settings')}>
      <Box className='flex flex-col gap-2'>
        <SwitchElement
          checked={instrumentSettings.attitudeIndicator}
          onChange={(e) => setInstrumentSettings({ ...instrumentSettings, attitudeIndicator: e })}
          label={t('crew_view.attitude_indicator')}
        />
        <SwitchElement
          checked={instrumentSettings.compass}
          onChange={(e) => setInstrumentSettings({ ...instrumentSettings, compass: e })}
          label={t('crew_view.compass')}
        />
        <SwitchElement
          checked={instrumentSettings.thrust}
          onChange={(e) => setInstrumentSettings({ ...instrumentSettings, thrust: e })}
          label={t('crew_view.thrust')}
        />
        <SwitchElement
          checked={instrumentSettings.battery}
          onChange={(e) => setInstrumentSettings({ ...instrumentSettings, battery: e })}
          label={t('crew_view.battery')}
        />
      </Box>
    </CrewModal>
  );
}

export default CrewInstrumentSettingsModal;
